import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { setLogout, setError as setSessionLost } from "features/auth/authSlice";
import { fetch, fetchGroups } from "./company-select.service";

const initialState = {
  companiesCollection: [],
  companiesCollectionRaw: [],
  isLoading: false,
  // Distinguishes "not asked yet" from "asked, and the answer was empty".
  // Without it the empty state renders on the very first paint, before the
  // fetch has even started, and tells a user who has companies that they
  // have none.
  hasLoaded: false,
  isError: false,
  errMsg: null,
  isUserMaster: false,
  // Groups where the viewer has group rights (GET /me/company-groups). A group
  // company with isMember === false is one the viewer may visit, not work in.
  companyGroups: [],
  hasLoadedGroups: false,
  // A failed load also leaves the groups empty, which says nothing about any company.
  didGroupsFail: false,
};

const CompanySelectSlice = createSlice({
  name: "companySelect",
  initialState,
  /* eslint-disable no-param-reassign */
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setCompanies(state, action) {
      state.isLoading = false;
      state.hasLoaded = true;
      state.companiesCollection = action.payload.companiesCollection;
      state.companiesCollectionRaw = action.payload.companiesCollectionRaw;
      state.isUserMaster = action.payload.isUserMaster;
    },
    setCompanyGroups(state, action) {
      state.companyGroups = action.payload;
      state.hasLoadedGroups = true;
      state.didGroupsFail = false;
    },
    setCompanyGroupsFailed(state) {
      state.companyGroups = [];
      state.hasLoadedGroups = true;
      state.didGroupsFail = true;
    },
    setError(state, action) {
      // isLoading was never cleared here, so a failed fetch left the UI
      // spinning forever. The payload is a plain message string — reading
      // .errMsg off it always yielded undefined, so nothing could be shown.
      state.isLoading = false;
      state.hasLoaded = true;
      state.isError = true;
      state.errMsg = typeof action.payload === 'string'
        ? action.payload
        : action.payload?.errMsg || null;
    },
  },
  /* eslint-enable */
  // One tab can serve two people in a row; the next one must not start from the
  // previous one's companies and groups, which decide member or visitor mode.
  extraReducers: (builder) => {
    builder.addCase(setLogout, () => initialState);
    builder.addCase(setSessionLost, () => initialState);
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = CompanySelectSlice;

// Extract and export each action creator by name
export const {
  setLoading,
  setCompanies,
  setCompanyGroups,
  setCompanyGroupsFailed,
  setError,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;

export const fetchCompanies = () => async (dispatch) => {
  dispatch(setLoading());
  try {
    const { companiesCollection, isUserMaster } = await fetch();
    dispatch(
      setCompanies({
        isUserMaster,
        companiesCollectionRaw: companiesCollection,
        companiesCollection: _.map(companiesCollection, (company) => ({
          label: company.name,
          value: company.id,
        })),
      })
    );
  } catch (e) {
    // Keep the raw reason for diagnosis, never as UI copy: axios yields things
    // like "Request failed with status code 401", and a hard-coded Lithuanian
    // fallback would surface in the English UI. The banner shows a translated
    // hint instead.
    // eslint-disable-next-line no-console
    console.error('[company-select] could not load companies:', e);
    dispatch(setError(e?.message || null));
  }
};

// Without groups the app works as before, so a failed load only logs (and is remembered,
// so that an empty list is not read as "no group company").
export const fetchCompanyGroups = () => async (dispatch) => {
  try {
    const { groups } = await fetchGroups();
    dispatch(setCompanyGroups(groups));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[company-select] could not load company groups:', e);
    dispatch(setCompanyGroupsFailed());
  }
};
